import { useState, useRef } from "react";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { importStudents } from "../lib/api";
import { ACADEMIC_YEARS, CURRENT_ACADEMIC_YEAR, GRADES } from "../lib/constants";

export default function ExcelImport() {
  const [target, setTarget] = useState("new");
  const [year, setYear] = useState(CURRENT_ACADEMIC_YEAR);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("الرجاء اختيار ملف Excel بصيغة .xlsx");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onSubmit = async () => {
    if (!file) { toast.error("اختر ملف Excel أولاً"); return; }
    setLoading(true);
    try {
      const data = await importStudents(file, target, year);
      setResult(data);
      toast.success(`تم استيراد ${data.inserted} سجل`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الاستيراد");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const isNew = target === "new";
    const headers = isNew
      ? ["اسم الطالب الرباعي", "رقم الهوية", "رقم الجوال", "الصف", "الفصل", "العام الدراسي", "تاريخ التسجيل"]
      : ["اسم الطالب الرباعي", "رقم الهوية", "الصف", "الفصل", "العام الدراسي", "تاريخ السحب"];

    const sampleGrade = GRADES[0];
    const today = new Date().toISOString().slice(0, 10);
    const samples = isNew
      ? [
          ["أحمد محمد علي الزهراني", "1012345678", "0501234567", sampleGrade, `${sampleGrade} -1`, CURRENT_ACADEMIC_YEAR, today],
          ["خالد عبدالله الحربي", "1023456789", "0502345678", sampleGrade, `${sampleGrade} -1`, CURRENT_ACADEMIC_YEAR, today],
          ["سعد فهد القحطاني", "", "0503456789", sampleGrade, `${sampleGrade} -2`, CURRENT_ACADEMIC_YEAR, today],
        ]
      : [
          ["أحمد محمد علي الزهراني", "1012345678", sampleGrade, `${sampleGrade} -1`, CURRENT_ACADEMIC_YEAR, today],
          ["خالد عبدالله الحربي", "1023456789", sampleGrade, `${sampleGrade} -1`, CURRENT_ACADEMIC_YEAR, today],
        ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...samples]);

    // RTL view
    ws["!views"] = [{ rightToLeft: true }];

    // Column widths
    ws["!cols"] = isNew
      ? [{ wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 26 }, { wch: 26 }, { wch: 14 }, { wch: 14 }]
      : [{ wch: 32 }, { wch: 14 }, { wch: 26 }, { wch: 26 }, { wch: 14 }, { wch: 14 }];

    // Style header row (font/fill via cell properties — XLSX SheetJS community supports basic styling)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[addr]) {
        ws[addr].s = {
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12, name: "Cairo" },
          fill: { fgColor: { rgb: "0F766E" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }
    // Row height for header
    ws["!rows"] = [{ hpt: 28 }];

    // Second sheet: instructions
    const instr = XLSX.utils.aoa_to_sheet([
      ["تعليمات الاستيراد"],
      [],
      ["1- لا تغيّر أسماء الأعمدة في الصف الأول"],
      ["2- (اسم الطالب، الصف، الفصل) حقول إجبارية — البقية اختيارية"],
      ["3- يجب أن يكون اسم الصف مطابقاً تماماً لقائمة الصفوف:"],
      ...GRADES.map(g => ["   • " + g]),
      [],
      ["4- صيغة الفصل: '<اسم الصف> -<رقم>'، مثال: " + `${sampleGrade} -1`],
      ["5- رقم الهوية بأرقام إنجليزية فقط (يمكن تركه فارغاً)"],
      ["6- التاريخ بصيغة YYYY-MM-DD مثال: " + today],
      ["7- إذا تكرر رقم الهوية فسيتم تخطي السجل تلقائياً"],
    ]);
    instr["!views"] = [{ rightToLeft: true }];
    instr["!cols"] = [{ wch: 70 }];
    if (instr["A1"]) {
      instr["A1"].s = { font: { bold: true, sz: 14, color: { rgb: "0F766E" } } };
    }

    XLSX.utils.book_append_sheet(wb, ws, isNew ? "المستجدون" : "المنسحبون");
    XLSX.utils.book_append_sheet(wb, instr, "التعليمات");

    XLSX.writeFile(wb, `قالب_${isNew ? "المستجدين" : "المنسحبين"}.xlsx`);
    toast.success("تم تنزيل القالب — افتحه في Excel وأدخل بياناتك ثم استورده");
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="استيراد الطلاب من Excel" subtitle="استيراد جماعي لبيانات الطلاب من ملف Excel" icon={FileSpreadsheet} />

      <Card className="p-5 sm:p-7 border-slate-200 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <div className="space-y-1.5">
            <Label>نوع الاستيراد</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="h-11" dir="rtl" data-testid="import-target"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="new">طلاب مستجدون</SelectItem>
                <SelectItem value="withdrawn">طلاب منسحبون</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>العام الدراسي الافتراضي</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-11" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                {ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>قالب جاهز</Label>
            <Button variant="outline" onClick={downloadTemplate} className="h-11 w-full gap-2" data-testid="btn-template">
              <FileDown size={16} /> تنزيل القالب
            </Button>
          </div>
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer rounded-2xl border-2 border-dashed border-teal-200 bg-teal-50/40 hover:bg-teal-50 transition-colors p-8 text-center"
          data-testid="dropzone"
        >
          <Upload className="mx-auto text-teal-700 mb-3" size={32} />
          <div className="font-cairo font-bold text-slate-800">اختر ملف Excel للاستيراد</div>
          <div className="text-xs text-slate-500 mt-1">صيغة .xlsx — يحتوي على: اسم الطالب، رقم الهوية، الصف، الفصل، رقم الجوال، التاريخ</div>
          {file && <div className="mt-3 text-sm text-teal-700 font-medium">{file.name}</div>}
          <Input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={onPick} className="hidden" data-testid="file-input" />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={onSubmit} disabled={!file || loading} className="bg-teal-700 hover:bg-teal-800 text-white gap-2 h-11 px-6 btn-elevate" data-testid="btn-import">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            {loading ? "جاري الاستيراد..." : "استيراد"}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5 border-slate-200" data-testid="import-result">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="rounded-xl bg-teal-50 border border-teal-200 p-4">
              <div className="flex items-center gap-2 text-teal-700 mb-1"><CheckCircle2 size={18} /> <span className="font-cairo font-bold">تم الاستيراد</span></div>
              <div className="text-3xl font-extrabold text-teal-800 tabular-nums">{result.inserted}</div>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-1"><AlertCircle size={18} /> <span className="font-cairo font-bold">تم التخطي</span></div>
              <div className="text-3xl font-extrabold text-amber-800 tabular-nums">{result.skipped}</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-cairo font-bold text-slate-700">سجلات تم تخطّيها</div>
              <ul className="text-xs divide-y divide-slate-100 max-h-72 overflow-auto">
                {result.errors.map((e, i) => (
                  <li key={i} className="px-3 py-2 flex justify-between gap-3">
                    <span className="text-slate-500">السطر {e.row}</span>
                    <span className="text-rose-600">{e.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
